import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Switch,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Globe,
  Plus,
  Edit2,
  Trash2,
  Image as ImageIcon,
} from 'lucide-react';
import {
  siteLanguagesService,
  getLanguageName,
} from 'components/api/siteLanguages';
import { SiteLanguage } from 'types/siteLanguage';
import { commonGridStyles } from 'styles/gridStyles';
import { showToast } from 'store/toastSlice';
import { useAppDispatch } from 'store/hooks';
import ConfirmDeleteModal from 'components/modals/ConfirmDeleteModal';
import AddLanguageModal from './AddLanguageModal';
import EditLanguageModal from './EditLanguageModal';
import UploadLogoModal from './UploadLogoModal';
import styles from './LangTab.module.scss';

interface LangTabProps {
  siteId: string | number;
}

const LangTab: React.FC<LangTabProps> = ({ siteId }) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SiteLanguage[]>([]);

  // Modals state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editLang, setEditLang] = useState<SiteLanguage | null>(null);
  const [logoLang, setLogoLang] = useState<SiteLanguage | null>(null);
  const [deleteLang, setDeleteLang] = useState<SiteLanguage | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchLanguages = useCallback(async () => {
    try {
      setLoading(true);
      const list = await siteLanguagesService.getSiteLanguages(siteId);
      setData(list || []);
    } catch (error) {
      console.error('Failed to fetch site languages:', error);
      dispatch(showToast({ text: 'Failed to fetch site languages', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [siteId, dispatch]);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  const handleToggle = async (id: number, currentStatus: boolean) => {
    try {
      // Optimistic update
      setData((prev) =>
        prev.map((row) => (row.id === id ? { ...row, isEnabled: !currentStatus } : row)),
      );

      await siteLanguagesService.toggleSiteLanguage(id, !currentStatus);
      dispatch(
        showToast({
          text: `Language ${!currentStatus ? 'enabled' : 'disabled'} successfully`,
          type: 'success',
        }),
      );
    } catch (error: unknown) {
      console.error('Failed to toggle site language:', error);
      let message = 'Failed to update language status';
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || error.message || message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      dispatch(showToast({ text: message, type: 'error' }));
      // Revert on error
      setData((prev) =>
        prev.map((row) => (row.id === id ? { ...row, isEnabled: currentStatus } : row)),
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteLang) return;

    try {
      setDeleting(true);
      await siteLanguagesService.deleteSiteLanguage(deleteLang.id);
      dispatch(showToast({ text: 'Site language removed successfully', type: 'success' }));
      setData((prev) => prev.filter((row) => row.id !== deleteLang.id));
      setDeleteLang(null);
    } catch (error: unknown) {
      console.error('Failed to delete site language:', error);
      let message = 'Failed to delete site language';
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || error.message || message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      dispatch(showToast({ text: message, type: 'error' }));
    } finally {
      setDeleting(false);
    }
  };


  const columns: GridColDef<SiteLanguage>[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 70,
        sortable: true,
      },
      {
        field: 'logo',
        headerName: 'Logo',
        width: 90,
        sortable: false,
        renderCell: (params) => {
          const row = params.row;
          const logoSrc = row.logoUrl;
          const code = row.code || '';

          return (
            <Tooltip title="Click to upload/replace logo" arrow>
              <Box
                className={styles.logoCell}
                onClick={(e) => {
                  e.stopPropagation();
                  setLogoLang(row);
                }}
              >
                {logoSrc ? (
                  <img src={logoSrc} alt={code} className={styles.logoImg} />
                ) : (
                  <div className={styles.logoPlaceholder}>
                    {code ? code.slice(0, 2) : <Globe size={16} />}
                  </div>
                )}
                <div className={styles.logoOverlay}>
                  <ImageIcon size={14} />
                </div>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'code',
        headerName: 'Code',
        width: 120,
        sortable: true,
        valueGetter: (_value, row) => (row.code || '').toUpperCase(),
        renderCell: (params) => (
          <span className={styles.codeBadge}>{params.value}</span>
        ),
      },
      {
        field: 'name',
        headerName: 'Language Name',
        flex: 1,
        minWidth: 160,
        sortable: true,
        valueGetter: (_value, row) => row.name || getLanguageName(row.code),
      },
      {
        field: 'isEnabled',
        headerName: 'Status',
        width: 130,
        sortable: true,
        renderCell: (params) => (
          <Switch
            checked={Boolean(params.value)}
            onChange={(e) => {
              e.stopPropagation();
              handleToggle(params.row.id, Boolean(params.value));
            }}
            color="primary"
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 150,
        sortable: false,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Box className={styles.actionBtnGroup} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Upload Logo" arrow>
                <IconButton
                  size="small"
                  className={`${styles.actionBtn} ${styles.logo}`}
                  onClick={() => setLogoLang(row)}
                >
                  <ImageIcon size={16} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Edit Details" arrow>
                <IconButton
                  size="small"
                  className={`${styles.actionBtn} ${styles.edit}`}
                  onClick={() => setEditLang(row)}
                >
                  <Edit2 size={16} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete Language" arrow>
                <IconButton
                  size="small"
                  className={`${styles.actionBtn} ${styles.delete}`}
                  onClick={() => setDeleteLang(row)}
                >
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [],
  );

  return (
    <Box className={styles.container}>
      {/* Header Toolbar */}
      <Box className={styles.header}>
        <Box className={styles.titleArea}>
          <div className={styles.titleIcon}>
            <Globe size={22} />
          </div>
          <h2 className={styles.title}>Site Languages</h2>
          <span className={styles.countBadge}>
            {data.length} {data.length === 1 ? 'language' : 'languages'}
          </span>
        </Box>

        <Box className={styles.toolbarActions}>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            className={styles.addBtn}
            onClick={() => setIsAddOpen(true)}
          >
            Add Language
          </Button>
        </Box>
      </Box>

      {/* Grid or Empty State */}
      {loading && data.length === 0 ? (
        <Box className={styles.loadingWrapper}>
          <CircularProgress size={38} sx={{ color: 'var(--accent-blue)' }} />
          <span>Loading site languages...</span>
        </Box>
      ) : data.length === 0 ? (
        <Box className={styles.emptyState}>
          <Globe size={48} className={styles.emptyIcon} />
          <div className={styles.emptyTitle}>No Languages Configured</div>
          <div className={styles.emptyDescription}>
            Add language localizations for this site so players can browse and interact in their preferred language.
          </div>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            className={styles.addBtn}
            onClick={() => setIsAddOpen(true)}
            sx={{ mt: 1 }}
          >
            Add Language
          </Button>
        </Box>
      ) : (
        <Box className={styles.gridWrapper}>
          <DataGrid
            rows={data}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            hideFooter={data.length <= 15}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            sx={commonGridStyles}
          />
        </Box>
      )}

      {/* Modals */}
      <AddLanguageModal
        open={isAddOpen}
        siteId={siteId}
        onClose={() => setIsAddOpen(false)}
        onSuccess={fetchLanguages}
      />

      <EditLanguageModal
        open={Boolean(editLang)}
        language={editLang}
        onClose={() => setEditLang(null)}
        onSuccess={fetchLanguages}
      />

      <UploadLogoModal
        open={Boolean(logoLang)}
        language={logoLang}
        onClose={() => setLogoLang(null)}
        onSuccess={fetchLanguages}
      />

      <ConfirmDeleteModal
        open={Boolean(deleteLang)}
        title={
          deleteLang
            ? `${deleteLang.name || getLanguageName(deleteLang.code)} (${(deleteLang.code || '').toUpperCase()})`
            : 'this language'
        }
        onClose={() => (deleting ? undefined : setDeleteLang(null))}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default LangTab;
